import { NOL_PRODUCT_CODE } from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { ITransformedMeal, ITransformedMealForLegs } from "../../Orchestrator/Types/Interface/IAddOns";
import { IBookingResponse } from "../../Orchestrator/Types/Index";

/** True when a transformed addon is a NOL product (code prefix on `type`). */
export const isNolMeal = (meal: ITransformedMeal): boolean =>
  (meal?.type || "").toUpperCase().startsWith(NOL_PRODUCT_CODE);

/** True when any option in a category is NOL — used to drop NOL from Extras. */
export const isNolCategoryOptions = (options: ITransformedMeal[]): boolean =>
  (options || []).some(isNolMeal);

/** Distinct NOL variants (by product code) available for a leg. */
export const nolProductsForLeg = (
  meals: ITransformedMealForLegs | undefined,
  legId: string
): ITransformedMeal[] => {
  const byCategory = meals?.[legId];
  if (!byCategory) return [];
  const flat = Object.values(byCategory).flat().filter(isNolMeal);
  const seen = new Set<string>();
  return flat.filter((m) => {
    const key = (m.type || "").toUpperCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Purchase-window validity: [from] + [days], at 23:59 local. */
export const nolValidUntil = (from: Date, days: number): Date => {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  d.setDate(d.getDate() + days);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 0, 0);
};

/**
 * True when a booked ancillary is a NOL day-pass — i.e. belongs to the
 * End-to-end Journey flow, not the "Extras" (meals/lounge/addon-shuttle)
 * surfaces. Resolves the product code the robust way: the reservation product
 * only carries `productId`, so the code lives on the OFFER's product list
 * (`products[].code`). Falls back to the raw ancillary `type` for wire shapes
 * that don't join cleanly. Pass the products from the offer that owns `anc`
 * (or a merged list when the product may live on a sibling offer).
 *
 * This is the single source of truth for NOL-ancillary detection — do not
 * re-inline `products[0].productCode || type` at call sites (that operand is
 * always undefined on the real shape, leaving detection on `type` alone).
 */
export const isNolAncillary = (
  offerProducts: { id?: string; code?: string }[] | undefined,
  anc: any
): boolean => {
  const ancProduct = (anc?.products || [])[0];
  // The product code can live in either place depending on the backend flow:
  // directly on the ancillary's product (`productCode`, typed-away but present
  // at runtime) or on the owning offer's product list (`code`, joined by
  // productId). Check both, then fall back to the raw ancillary `type`.
  const offerCode = (offerProducts || []).find((p) => p?.id === ancProduct?.productId)?.code;
  const code = ancProduct?.productCode || offerCode || anc?.type || "";
  return String(code).toUpperCase().startsWith(NOL_PRODUCT_CODE);
};

/** Booked NOL ancillaries on a leg → {bookedOfferId, ancillaryId} for deletion. */
export const extractBookedNolAncillaries = (
  booking: IBookingResponse | null,
  legId: string
): { bookedOfferId: string; ancillaryId: string }[] => {
  const offers = (booking?.booking as any)?.bookedOffers || [];
  const out: { bookedOfferId: string; ancillaryId: string }[] = [];
  for (const offer of offers) {
    for (const anc of offer?.ancillaries || []) {
      const onLeg = (anc?.tripCoverage?.coveredLegIds || []).includes(legId);
      if (anc?.id && onLeg && isNolAncillary(offer?.products, anc)) {
        out.push({ bookedOfferId: offer.offerId, ancillaryId: anc.id });
      }
    }
  }
  return out;
};
